import React from "react";
import { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { TFunction } from "i18next";

import { getFormatDate } from "~/libs/getFormatDate";

/** Колонки справочника адресов, по которым возможна клиентская сортировка */
export const ADDRESS_SELECTION_GUIDE_SORTABLE_COLUMNS = [
  "id",
  "parentId",
  "name",
  "type",
  "socr",
  "index",
  "cladrCode",
  "okatoCode",
  "fiasId",
  "fiasValue",
  "active",
];

const localeCompare = (a: unknown, b: unknown) =>
  String(a ?? "").localeCompare(String(b ?? ""));

const numberCompare = (a: unknown, b: unknown) =>
  (Number(a) || 0) - (Number(b) || 0);

const COMPARATORS: Record<string, (a: any, b: any) => number> = {
  id: (a, b) => numberCompare(a.id, b.id),
  parentId: (a, b) => numberCompare(a.parentId, b.parentId),
  name: (a, b) => localeCompare(a.name, b.name),
  type: (a, b) => localeCompare(a.type, b.type),
  socr: (a, b) => localeCompare(a.socr, b.socr),
  index: (a, b) => localeCompare(a.index, b.index),
  cladrCode: (a, b) => localeCompare(a.cladrCode, b.cladrCode),
  okatoCode: (a, b) => localeCompare(a.okatoCode, b.okatoCode),
  fiasId: (a, b) => localeCompare(a.fiasId, b.fiasId),
  fiasValue: (a, b) => localeCompare(a.fiasValue, b.fiasValue),
  active: (a, b) => Number(a.active) - Number(b.active),
};

export const getAddressSelectionGuideColumns = (
  t: TFunction,
  options: string[],
  sortField?: string,
  sortType?: string,
): ColumnsType<any> =>
  options.map((option) => {
    const comparator = COMPARATORS[option];
    const sortOrder: SortOrder | undefined =
      comparator &&
      sortField === option &&
      (sortType === "ASC" || sortType === "DESC")
        ? sortType === "DESC"
          ? "descend"
          : "ascend"
        : undefined;

    const baseColumn = {
      title: t(`addressSelectionGuide.table.${option}`),
      dataIndex: option,
      key: option,
      ...(comparator ? { sorter: comparator, sortOrder } : {}),
    };

    // Форматирование дат.
    if (option === "updatedAt" || option === "createdAt") {
      return {
        ...baseColumn,
        render: (value: any) => <span>{getFormatDate(value)}</span>,
      };
    }

    // Флаг активности приходит как boolean — рендерим как текст.
    if (option === "active") {
      return {
        ...baseColumn,
        render: (value: any) => (
          <span>
            {value
              ? t("addressSelectionGuide.activeYes")
              : t("addressSelectionGuide.activeNo")}
          </span>
        ),
      };
    }

    // Тип адреса — переводим из enum значения.
    if (option === "type") {
      return {
        ...baseColumn,
        render: (value: any) =>
          value ? t(`addressSelectionGuide.typeOptions.${value}`) : "",
      };
    }

    return {
      ...baseColumn,
      render: (value: any) => (
        <span>
          {value === null || value === undefined ? "" : String(value)}
        </span>
      ),
    };
  });
